import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Row, Col } from "react-bootstrap";
import { FaArrowLeftLong } from "react-icons/fa6";
import { Box, Tooltip, IconButton } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import Swal from "sweetalert2";

import Button from "../../components/common/Button/Button";
import ReusableMuiTable from "../../components/common/Table/Table";
import FormInput from "../../components/common/FormInput/FormInput";
import UploadImage from "../../components/common/UploadImage/UplaodImage";
import { showToast } from "../../components/common/ToastNotification/ToastNotification";

import {
  getMagnusteam,
  addTeamMember,
  updateTeamMember,
  deleteTeamMember,
  getTeamMemberById,
} from "../../api/service/magnus-team.service";

const MagnusTeam = () => {
  const navigate = useNavigate();

  const [teamList, setTeamList] = useState([]);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");

  const [editingId, setEditingId] = useState(null);
  const [existingImage, setExistingImage] = useState("");
  const [teamImage, setTeamImage] = useState(null);

  const [formValue, setFormValue] = useState({
    name: "",
    designation: "",
  });

  const fetchTeam = useCallback(async () => {
    try {
      setLoading(true);
      const res = await getMagnusteam({ 
        page, 
        limit: 10, 
        search: searchText 
      });
      if (res?.success) {
        setTeamList(res.data.data || []);
        setTotalCount(res.data.total || 0);
      }
    } catch {
      showToast.error("Failed to load team members");
    } finally {
      setLoading(false);
    }
  }, [page, searchText]);

  useEffect(() => {
    fetchTeam();
  }, [fetchTeam]);

  const handleFileChange = (file) => {
    const selectedFile = Array.isArray(file) ? file[0] : file;
    setTeamImage(selectedFile);
  };

  const handleEdit = async (row) => {
    try {
      setLoading(true);
      const res = await getTeamMemberById(row.id);

      if (res?.success) {
        setFormValue({
          name: res.data.name || "",
          designation: res.data.designation || "",
        });
        setExistingImage(res?.data?.photo?.url || "");
        setTeamImage(null);
        setEditingId(row.id);
      }
    } catch {
      showToast.error("Error fetching member details");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (row) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "This team member will be permanently deleted!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d32f2f",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, delete it",
    });

    if (!result.isConfirmed) return;

    try {
      setLoading(true);
      const res = await deleteTeamMember(row.id);
      if (res?.success) {
        showToast.success(res.message || "Deleted successfully");
        fetchTeam();
      }
    } catch {
      showToast.error("Delete failed");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!formValue.name || !formValue.designation) {
      return showToast.error("Name and Designation are required");
    }

    const formData = new FormData();
    formData.append("name", formValue.name);
    formData.append("designation", formValue.designation);

    if (teamImage) {
      formData.append("image", teamImage);
    }

    if (editingId) {
      formData.append("team_id", editingId);
    }

    try {
      setLoading(true);
      const res = editingId
        ? await updateTeamMember(formData)
        : await addTeamMember(formData);

      if (res?.success) {
        showToast.success(res.message);
        setFormValue({ name: "", designation: "" });
        setExistingImage("");
        setTeamImage(null);
        setEditingId(null);
        fetchTeam();
      }
    } catch {
      showToast.error("Operation failed");
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (searchValue) => {
    setSearchText(searchValue);
    setPage(1); 
  };

  const columns = [
    { name: "name", label: "Name" },
    { name: "designation", label: "Designation" },
    {
      name: "actions",
      label: "Action",
      options: {
        customBodyRenderLite: (index) => {
          const row = teamList[index];
          return (
            <Box display="flex" gap={1}>
              <Tooltip title="Edit">
                <IconButton
                  onClick={() => handleEdit(row)}
                  sx={{ color: "#C39B22" }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>

              <Tooltip title="Delete">
                <IconButton
                  onClick={() => handleDelete(row)}
                  sx={{ color: "#d32f2f" }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          );
        },
      },
    },
  ];

  return (
    <div className="py-3">
      <div
        className="d-flex align-items-center gap-2 mb-3"
        onClick={() => navigate(-1)}
        style={{ cursor: "pointer" }}
      >
        <FaArrowLeftLong />
        <h3 className="management-tile mb-0">Team Management</h3>
      </div>

      <Row className="mb-3">
        <Col md={6}>
          <FormInput
            label="Name"
            value={formValue.name}
            onChange={(e) =>
              setFormValue({ ...formValue, name: e.target.value })
            }
            placeholder="Enter name"
          />
        </Col>

        <Col md={6}>
          <FormInput
            label="Designation"
            value={formValue.designation}
            onChange={(e) =>
              setFormValue({ ...formValue, designation: e.target.value })
            }
            placeholder="Enter designation"
          />
        </Col>

        <Col xs={12}>
          <label className="form-label">Profile Image</label>
          <UploadImage
            onFileSelect={handleFileChange}
            existingImages={existingImage}
            multiple={false}
          />
        </Col>

        <Col xs={12} className="mt-3">
          <Button onClick={handleSubmit} disabled={loading}>
            {editingId ? "Update Member" : "Add Member"}
          </Button>
        </Col>
      </Row>

      <ReusableMuiTable
        data={teamList}
        columns={columns}
        loading={loading}
        options={{
          serverSide: true,
          count: totalCount,
          rowsPerPage: 10,
          selectableRows: "none",
          search: true,
          searchText: searchText,
          searchPlaceholder: "Search team members...",
          onSearchChange: handleSearchChange,
          onChangePage: (p) => setPage(p + 1),
        }}
      />
    </div>
  );
};

export default MagnusTeam;